import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Nps = () => {
  // State variables for NPS calculation
  const [promoters, setPromoters] = useState(0); // Number of Promoters
  const [detractors, setDetractors] = useState(0); // Number of Detractors
  const [respondents, setRespondents] = useState(0); // Total number of respondents
  const [nps, setNps] = useState(0); // Calculated NPS

  // Event handlers to update state values
  const handlePromotersChange = (e) =>
    setPromoters(parseFloat(e.target.value) || 0);
  const handleDetractorsChange = (e) =>
    setDetractors(parseFloat(e.target.value) || 0);
  const handleRespondentsChange = (e) =>
    setRespondents(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate NPS when any input changes
  useEffect(() => {
    if (respondents > 0) {
      const promotersPercentage = (promoters / respondents) * 100;
      const detractorsPercentage = (detractors / respondents) * 100;
      const npsScore = promotersPercentage - detractorsPercentage;
      setNps(npsScore);
    } else {
      setNps(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [promoters, detractors, respondents]);

  return (
    <Layout title="Net Promoter Score (NPS) Calculator" path="nps-calculator">
      <section className="section-padding" id="nps-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* NPS Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Net Promoter Score (NPS) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Promoters Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="promoters" className="form-label">
                        Number of Promoters
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Promoters:</strong> Promoters are customers who rate your product or service 9 or 10 on a 10-point scale."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="promoters"
                        placeholder="Enter number of promoters"
                        value={promoters || ""}
                        onChange={handlePromotersChange}
                      />
                    </div>

                    {/* Detractors Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="detractors" className="form-label">
                        Number of Detractors
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Detractors:</strong> Detractors are customers who rate your product or service 0 to 6 on a 10-point scale."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="detractors"
                        placeholder="Enter number of detractors"
                        value={detractors || ""}
                        onChange={handleDetractorsChange}
                      />
                    </div>

                    {/* Total Respondents Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="respondents" className="form-label">
                        Total Number of Respondents
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Respondents:</strong> Respondents are the total number of customers who participated in the survey."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="respondents"
                        placeholder="Enter total number of respondents"
                        value={respondents || ""}
                        onChange={handleRespondentsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Net Promoter Score (NPS):{" "}
                        <span className="fw-bold ms-3">
                          {nps === 0
                            ? "0.00"
                            : nps.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Net Promoter Score (NPS) Calculator
                </h3>

                {/* What is a Net Promoter Score (NPS) Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Net Promoter Score (NPS) Calculator?
                </h4>
                <p>
                  A **Net Promoter Score (NPS) Calculator** helps businesses
                  measure customer loyalty and satisfaction by calculating the
                  percentage of promoters and detractors from survey responses.
                  It provides insight into the likelihood of customers
                  recommending your product or service.
                </p>

                {/* Why Use an NPS Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Net Promoter Score (NPS) Calculator?
                </h4>
                <p>
                  Using this calculator helps track customer satisfaction levels
                  and can provide actionable insights into how well a business
                  is performing in the eyes of its customers. It is especially
                  useful for identifying areas that need improvement in customer
                  service, product quality, or customer experience.
                </p>

                {/* Benefits of Using an NPS Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an NPS Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Track Customer Satisfaction and Loyalty:
                    </strong>{" "}
                    NPS provides a simple, effective metric for understanding
                    customer sentiment.
                  </li>
                  <li>
                    <strong>
                      Provides Actionable Insights for Improvement:
                    </strong>{" "}
                    The calculator helps identify detractors and promoters,
                    offering actionable insights for improving customer service.
                  </li>
                  <li>
                    <strong>Supports Brand Reputation Management:</strong>{" "}
                    Monitoring NPS helps manage and improve brand reputation by
                    addressing customer concerns proactively.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Net Promoter Score (NPS) Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Customer Service Teams:</strong> Helps understand
                    customer satisfaction and loyalty, guiding improvements in
                    customer service.
                  </li>
                  <li>
                    <strong>Marketing Teams:</strong> Provides insights into how
                    customers perceive the brand, aiding in marketing and
                    communication strategies.
                  </li>
                  <li>
                    <strong>Product Management Teams:</strong> Helps gauge
                    customer feedback on products, guiding product improvements
                    or innovations.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is a good NPS score?</h4>
                <p>
                  A good NPS score depends on the industry, but generally, a
                  score above 50 is considered excellent, while a score below 0
                  is considered poor. A higher NPS indicates higher customer
                  loyalty.
                </p>

                <h4 className="mt-4 mb-3">How can I improve my NPS score?</h4>
                <p>
                  To improve your NPS score, focus on turning detractors into
                  promoters by improving customer experience, addressing
                  complaints promptly, and delivering high-quality products and
                  services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Nps;
